<template>
  <div class="drag-container" v-drag-and-drop:options="options">
    <div class="row title-input-container">
      <div class="col-sm-6 page-row center-content">
        <p class="inline-label">Step Title:</p>
        <input
          class="title-input"
          type="text"
          v-model="stepTitle"
          @input="updateStepTitle"
        />
      </div>
      <div class="col-sm-6 page-row center-content">
        <p class="inline-label">Description:</p>
        <input
          class="title-input"
          type="text"
          v-model="stepDescription"
          @input="updateStepDescription"
        />
      </div>
    </div>

    <div class="page-row tool-wrapper">
      <div class="page-col">
        <button class="my-btn" @click="saveStep">Save</button>
        <button class="my-btn" @click="showSameAsStepModal = true">
          Same as step...
        </button>
      </div>
      <div class="page-col menu-container">
        <button class="my-btn">Delete this Step</button>
        <button class="my-btn" @click="showBackToOverviewModal = true">
          Exit Step
        </button>
      </div>
    </div>

    <div class="drag-content">
      <div class="drag-list-container">
        <ul class="drag-list">
          <li class="drag-column" v-for="group in groups" :key="group.id">
            <span
              @click="group.showItems = !group.showItems"
              class="drag-column-header"
            >
              <h2>{{ group.name }}</h2>
              <span class="drag-column-btn"
                ><img src="../assets/moreVerticalIcon.svg"
              /></span>
            </span>
            <ul
              v-show="group.showItems"
              class="drag-inner-list"
              :class="{ 'dropzone-list': group.dropzone }"
              :data-id="group.id"
            >
              <li
                class="drag-item"
                v-for="item in group.items"
                :key="item.id"
                :data-id="item.id"
              >
                <component
                  :is="item.iconComponent"
                  :size="30"
                  v-if="!item.iconComponent.includes('mdi')"
                ></component>

                <v-icon v-else color="white" class="widget-icon">
                  {{ item.iconComponent }}
                </v-icon>
                <div class="drag-item-text">{{ item.name }}</div>
              </li>
            </ul>
          </li>
        </ul>
      </div>
      <div class="drop-list-container">
        <ul class="drop-list">
          <li
            class="drop-column"
            v-for="(group, groupId) in editingStep.channels"
            :key="group.id"
          >
            <span class="drop-column-header">
              <h2>{{ group.name }}</h2>
              <img src="../assets/moreVerticalIcon.svg" />
            </span>
            <ul
              class="drop-inner-list"
              :class="{ 'dropzone-list': group.dropzone }"
              :data-id="group.id"
              @added="added"
              @removed="removed($event, group, groupId)"
              @reordered="reordered($event, group, groupId)"
            >
              <li
                class="drag-item"
                v-for="(item, itemId) in group.items"
                :key="item.id"
                :data-id="item.id"
              >
                <component
                  :is="item.iconComponent"
                  :size="30"
                  v-if="!item.iconComponent.includes('mdi')"
                ></component>

                <v-icon v-else color="white" class="widget-icon">
                  {{ item.iconComponent }}
                </v-icon>
                <div class="drag-item-text">{{ item.name }}</div>

                <div class="flex-grow-1 d-flex align-center justify-end">
                  <v-btn
                    icon
                    x-small
                    @click.stop="editItem(group, item)"
                    v-if="item.id.includes('widget-')"
                  >
                    <v-icon x-small color="white"> mdi-pencil </v-icon>
                  </v-btn>

                  <v-btn icon x-small @click.stop="deleteItem(groupId, itemId)">
                    <v-icon small color="white"> mdi-close </v-icon>
                  </v-btn>
                </div>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </div>
    <modal
      v-if="showWarning"
      :withOkBtn="true"
      :withCancelBtn="false"
      @ok="showWarning = false"
    >
      <div slot="header">Warning</div>
      <div slot="body">You cannot drop this item here!</div>
    </modal>

    <modal
      v-if="showSaveStepModal"
      :withOkBtn="true"
      :withCancelBtn="false"
      @ok="showSaveStepModal = false"
    >
      <div slot="header">Save step</div>
      <div slot="body">This step is saved.</div>
    </modal>

    <modal
      v-if="showBackToOverviewModal"
      :withOkBtn="true"
      :withCancelBtn="true"
      @ok="backToOverview"
      @close="showBackToOverviewModal = false"
    >
      <div slot="header">Back to experience</div>
      <div slot="body">Save this step and go back to experience overview?</div>
    </modal>

    <same-step-modal
      v-if="showSameAsStepModal"
      @close="showSameAsStepModal = false"
      @ok="sameAsStep"
    ></same-step-modal>

    <v-dialog v-model="settingsDialog.open" max-width="1000px">
      <div
        class="secondary d-flex flex-column scrollable-dialog"
        v-if="settingsDialog.open"
      >
        <v-btn
          icon
          small
          @click="settingsDialog.open = false"
          class="ml-auto mt-1 mr-1"
        >
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>

        <div class="pa-4 pt-0 d-flex flex-column secondary">
          <component
            :is="settingsDialog.component"
            :groupName="settingsDialog.groupName"
            :itemName="settingsDialog.itemName"
          />
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import Modal from "@/components/modals/Modal";
import SameStepModal from "@/components/modals/SameStepModal.vue";
import { mapGetters } from "vuex";
// Icons
import ApplicationsIcon from "@/components/icons/Applications.vue";
import ButtonIcon from "@/components/icons/Button.vue";
import CircleIcon from "@/components/icons/Circle.vue";
import ImageGalleryIcon from "@/components/icons/ImageGallery.vue";
import ImagesIcon from "@/components/icons/Images.vue";
import LayoutIcon from "@/components/icons/Layout.vue";
import MusicIcon from "@/components/icons/Music.vue";
import QuizIcon from "@/components/icons/Quiz.vue";
import ScreenTapIcon from "@/components/icons/ScreenTap.vue";
import TimelineIcon from "@/components/icons/Timeline.vue";
import VideoIcon from "@/components/icons/Video.vue";

import Gallery from "@/components/widget-editors/Gallery.vue";
import Quiz from "@/components/widget-editors/Quiz.vue";
import Map from "@/components/widget-editors/Map.vue";
import VideoPlayer from "@/components/widget-editors/VideoPlayer.vue";
import AudioPlayer from "@/components/widget-editors/AudioPlayer.vue";
import ButtonBar from "@/components/widget-editors/ButtonBar.vue";

import groups from "../assets/data/groups.json";

export default {
  computed: {
    ...mapGetters(["editingStep"]),
  },
  components: {
    modal: Modal,
    SameStepModal,
    ApplicationsIcon,
    ButtonIcon,
    CircleIcon,
    ImageGalleryIcon,
    ImagesIcon,
    LayoutIcon,
    MusicIcon,
    QuizIcon,
    ScreenTapIcon,
    TimelineIcon,
    VideoIcon,
    Gallery,
    Quiz,
    Map,
    VideoPlayer,
    AudioPlayer,
    ButtonBar,
  },
  data() {
    return {
      showHelp: false,
      showWarning: false,
      showSaveStepModal: false,
      showBackToOverviewModal: false,
      showSameAsStepModal: false,
      stepTitle: "",
      stepDescription: "",
      options: {
        dropzoneSelector: ".dropzone-list",
        draggableSelector: ".drag-item",
        showDropzoneAreas: true,
        multipleDropzonesItemsDraggingEnabled: false,
        onDrop: function (event) {
          let itemId = event.items[0].attributes["data-id"].value;
          let itemObj = null;
          for (let k = 0; k < this.groups.length; k++) {
            let items = this.groups[k].items;
            for (let j = 0; j < items.length; j++) {
              if (items[j].id == itemId) {
                itemObj = items[j];
              }
            }
          }
          // -------------------------------------------------------------------------------------------
          let dropListId = event.droptarget.attributes["data-id"].value; // ex. 'channel-1'
          let sourceListId =
            event.items[0].parentNode.attributes["data-id"].value; // ex. 'group-1'
          let groupId = itemObj.groupId;
          if (dropListId != sourceListId) {
            let dropListIndex = null;
            let updateDropList = this.editingStep.channels.filter(
              (channel, index) => {
                if (channel.id == dropListId) {
                  dropListIndex = index;
                }
                return channel.id == dropListId;
              }
            )[0];
            let group = this.groups.filter((group) => {
              return group.id == groupId;
            })[0];
            let droppingItem = group.items.filter((item) => {
              return item.id == itemId;
            })[0];
            if (updateDropList[droppingItem.type]) {
              console.log("you can drop item here...");
              let assetExists = updateDropList.items.filter(function (el) {
                return el.id == itemId;
              });
              console.log(assetExists);
              if (assetExists.length == 0) {
                this.addChannelItem(dropListIndex, droppingItem);
              } else {
                console.log("asset already exists.");
              }
            } else {
              this.showWarning = true;
            }
          } else {
            console.log("reordering items");
          }
        },
      },
      groups: groups.groups,
      settingsDialog: {
        open: false,
        groupName: null,
        itemName: null,
        component: null,
      },
      components: {
        "widget-1": "Gallery",
        "widget-2": "ButtonBar",
        "widget-3": "VideoPlayer",
        "widget-4": "AudioPlayer",
        "widget-5": "Quiz",
        "widget-6": "Map",
      },
    };
  },
  mounted() {
    this.stepTitle = this.editingStep.title;
    this.stepDescription = this.editingStep.description;
  },
  methods: {
    backToOverview() {
      this.$store
        .dispatch({
          type: "saveSceneNode",
          nodeObj: this.editingStep,
        })
        .then((response) => {
          this.$router.push({ path: "experience_overview" });
        });
    },
    hideHelp() {
      this.showHelp = false;
    },
    deleteItem(channelIndex, itemIndex) {
      this.$store.dispatch({
        type: "deleteChannelItem",
        channelIndex: channelIndex,
        itemIndex: itemIndex,
      });
    },
    editItem(group, item) {
      console.log(group);
      console.log(item);

      this.settingsDialog = {
        open: true,
        groupName: group.name,
        itemName: item.name,
        component: this.components[item.id],
      };
    },
    added(event) {
      console.log("added Item");
      // CHECK FOR MULTIPLE ASSETS ?
    },
    removed(event, group, groupId) {
      let itemId = event.detail.ids[0];
      let itemIndex = group.items.findIndex((item) => item.id == itemId);
      this.deleteItem(groupId, itemIndex);
    },
    reordered(event, group, groupId) {
      let itemId = event.detail.ids[0];
      let newIndex = event.detail.index;
      let itemIndex = group.items.findIndex((item) => item.id == itemId);
      this.$store.dispatch({
        type: "reorderChannelItems",
        channelIndex: groupId,
        fromIndex: itemIndex,
        toIndex: newIndex,
      });
    },
    updateStepTitle() {
      this.$store.dispatch({
        type: "updateStepTitle",
        title: this.stepTitle,
      });
    },
    updateStepDescription() {
      this.$store.dispatch({
        type: "updateStepDescription",
        description: this.stepDescription,
      });
    },
    addChannelItem(channelIndex, channelObj) {
      console.log(channelIndex);
      this.$store.dispatch({
        type: "addChannelItem",
        channelIndex: channelIndex,
        channelObj: channelObj,
      });
    },
    saveStep() {
      this.$store
        .dispatch({
          type: "saveSceneNode",
          nodeObj: this.editingStep,
        })
        .then((response) => {
          this.showSaveStepModal = true;
        });
    },
    sameAsStep(index) {
      console.log(index);
      this.showSameAsStepModal = false;
    },
  },
  watch: {
    "settingsDialog.open"(newValue) {
      if (!newValue) {
        this.settingsDialog = {
          open: false,
          groupName: null,
          itemName: null,
        };
      }
    },
  },
};
</script>

<style scoped lang="scss">
$ease-out: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
$to-do: #f4ce46;
$in-progress: #2a92bf;
$approved: #00b961;

ul {
  list-style-type: none;
  margin: 0;
  padding: 0;
}

.drag-container {
  width: 100%;
  height: 100%;
  overflow: hidden;
  padding-top: 80px;
}

.title-input-container {
  left: 30%;
}

.node-title {
  text-align: center;
  padding: 20px 0;
}

.node-title h1 {
  margin: 0;
}

.node-title span {
  font-weight: 400;
}

.drag-content {
  display: flex;
  margin-top: 20px;
}

.buttons-container {
  display: flex;
  position: relative;
  margin: 0 0 20px;
}

.drag-list-paragraph {
  margin: 0;
  padding: 10px;
  position: absolute;
  top: 64px;
  left: 0;
  font-size: 14px;
  border-radius: 4px;
  background-color: rgba(92, 193, 166, 1);
  z-index: 4;
}

.drag-list-paragraph:after {
  content: " ";
  position: absolute;
  bottom: 100%;
  left: 12%;
  margin-left: -5px;
  border-width: 12px;
  border-style: solid;
  border-color: transparent transparent #5cc1a6 transparent;
  opacity: 1;
}

.edit-node-btn {
  flex: 2;
  padding: 10px 20px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  color: white;
  background-color: #5cc1a6;
  border: 2px solid #5cc1a6;
  border-radius: 30px;
  cursor: pointer;
}

.edit-node-btn:focus {
  outline: none;
  border: 2px solid #3fa68b;
}

.help-btn {
  flex: 1;
  background: none;
  border: none;
  cursor: pointer;
}

.help-btn img {
  width: 40px;
  height: 40px;
}

.help-btn:focus {
  outline: none;
}

.drag-list-container {
  flex: 1;
  padding: 0 10px;
}

.drag-list {
  display: block;
  max-height: 70vh;
  padding-right: 10px;
  overflow-y: auto;
  overflow-x: hidden;

  @media (max-width: 690px) {
    display: block;
  }
}

.drag-column {
  flex: 1;
  border: 1px solid rgba(white, 0.5);
  border-radius: 4px;
  position: relative;
  background: rgba(black, 0.2);
  overflow: hidden;
  margin-bottom: 10px;

  @media (max-width: 690px) {
    margin-bottom: 30px;
  }

  h2 {
    font-size: 0.8rem;
    margin: 0;
    text-transform: uppercase;
    font-weight: 600;
  }

  .drag-column-header {
    cursor: pointer;
    background-color: rgba(white, 0.2);
  }

  &-to-do {
    .drag-column-header,
    .drag-options {
      background: $to-do;
    }
  }

  &-in-progress {
    .drag-column-header,
    .drag-options {
      background: $in-progress;
    }
  }

  &-approved {
    .drag-column-header,
    .drag-options {
      background: $approved;
    }
  }
}

.drag-column:hover {
  // transform: scale(1.02);
  background: rgba(230, 230, 230, 0.2);
}

.drop-list-container {
  flex: 5;
}

.drop-list {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;

  @media (max-width: 690px) {
    display: block;
  }
}

.drop-column {
  flex: 0 1 250px;
  margin: 0 10px 10px;
  position: relative;
  background: rgba(black, 0.2);
  overflow: hidden;

  @media (max-width: 690px) {
    margin-bottom: 30px;
  }

  h2 {
    font-size: 0.8rem;
    margin: 0;
    text-transform: uppercase;
    font-weight: 600;
  }

  &-to-do {
    .drop-column-header,
    .drag-options {
      background: $to-do;
    }
  }

  &-in-progress {
    .drop-column-header,
    .drag-options {
      background: $in-progress;
    }
  }

  &-approved {
    .drop-column-header,
    .drag-options {
      background: $approved;
    }
  }
}

.drop-column:hover {
  transform: scale(1.02);
  background: rgba(230, 230, 230, 0.2);
}

.drag-column-header,
.drop-column-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px;
  user-select: none;
}

.drop-inner-list {
  height: 200px;
  overflow: auto;
}

.drag-item {
  margin: 10px;
  // height: 100px;
  padding: 10px;
  background: rgba(black, 0.4);
  transition: $ease-out;
  position: relative;
  display: flex;
  justify-content: flex-start;
  align-items: center;

  /* items grabbed state */
  &[aria-grabbed="true"] {
    background: #5cc1a6;
    color: #fff;
  }

  .drag-item-text {
    font-size: 1rem;
    padding-left: 1rem;
    // padding-top: 1rem;
  }

  .drag-icon img {
    vertical-align: text-top;
  }

  .delete-btn {
    position: absolute;
    top: 10px;
    right: 10px;
    background: none;
    border: none;
    width: 12px;
    height: 12px;
    padding: 0;
    img {
      width: 100%;
      height: 100%;
    }
  }
}

.drag-item:hover {
  background-color: rgba(230, 230, 230, 0.2);
}

.drag-header-more {
  cursor: pointer;
}

.inline-label {
  padding-right: 10px;
  margin: auto 0;
}

.widget-icon {
  height: 30px;
  width: 30px;
  font-size: 30px;
}
</style>
